import { z } from "zod";

/**
 * Decision mails: a diver gets an email when staff decide on their suggestion. Owner
 * (2026-11-04): "email on decision". This block is binding for API and client.
 *
 * When:
 * - Approve (with or without adjustments) and reject of a suggestion, i.e. the actions `approve` /
 *   `reject` in the audit log. Never for withdraw, never for submit, never retroactively for
 *   suggestions decided before this change.
 * - Only to the submitter, and only if the account still exists, is not banned, its email is
 *   verified, and `decisionMails` is true. Staff cannot submit suggestions; a diver promoted to
 *   staff while one was pending still gets the mail for it (it is their own suggestion; the role
 *   is not checked when the mail is built).
 * - One mail per decision. It is sent AFTER the decision's transaction commits, fire-and-forget
 *   (`void mailer.…`, never awaited): a mail failure is logged and never fails, delays or rolls
 *   back the decision. Not audited (a mail is not a write; the decision row is the record).
 *
 * Content (de/en/fr/it, plain text plus simple HTML, everything escaped):
 * - Subject: accepted / accepted with changes / not accepted, plus the kind ("Neuer Tauchplatz",
 *   "Neues Objekt", "Änderung an einem Tauchplatz", "Änderung an einem Objekt") and the target's
 *   name: the RESULT's name as stored after the decision (after an adjusted approval the adjusted
 *   name; for a rejection the proposed name, or the target's current name for an edit),
 *   `pickLocale` in the mail's locale.
 * - Header hygiene: names are diver input and may contain line breaks. In the subject, every
 *   control character (`[\u0000-\u001f\u007f]`) becomes a space, whitespace is collapsed and the
 *   subject is capped at 150 characters (ellipsis). The HTML body escapes everything; the plain-text
 *   body drops control characters other than `\n`.
 * - Body: the same facts, the staff `reason` for a rejection if given (it is written for the diver;
 *   the reject form says so), the "adjusted" note for an approval with changes, and links:
 *   approved → the spot page (`/<lang>/spot/<slug>`; for an object, its spot) ONLY if that spot
 *   (and, for an object, the object) is published and not deleted when the mail is built (an
 *   approval may create a draft, and a spot may be unpublished before the mail goes out);
 *   otherwise, and for a rejection, the diver's suggestion list (`/<lang>/konto/vorschlaege`).
 *   Plus a footer link to the account page
 *   where these mails are switched off (`/<lang>/konto#benachrichtigungen`; the client keeps the hash
 *   through the login redirect and lands on the checkbox). Links use the app origin (APP_ORIGIN).
 * - Never the diver's own `note` or payload, never staff names, emails or internal notes.
 * - Locale: `suggestions.locale`, the UI language the diver submitted in (SuggestionCreate
 *   `locale`, optional, default de). Existing suggestions have none → de.
 *
 * Opt-out (per account, default ON):
 * - `GET /api/me/notifications` → `MeNotifications` `{ decisionMails }`, `PATCH` with
 *   `MeNotificationsUpdate` → the new value. Auth "diver" (staff may read and set it; it has no
 *   effect for them). Private: `Cache-Control: private, no-cache`, `Vary: Cookie` on every answer,
 *   never cached by the service worker (the `/api/me` rule). Writes need the Origin check and are
 *   rate-limited after auth, per user id (keyed limiter "notifications", 30/min → 429 with
 *   Retry-After). Not audited (a personal preference, like favourites).
 * - Kept OUT of `Me` on purpose: `Me` is strict and cached older bundles must keep parsing
 *   /api/me (same reason as `MeFeatures`).
 * - Stored as `user.decision_mails boolean NOT NULL DEFAULT true` (existing divers: on).
 * - Account page: a checkbox "E-Mail, wenn über meine Vorschläge entschieden wurde", shown to
 *   divers only (staff never get these mails).
 */
export const MeNotifications = z.object({ decisionMails: z.boolean() }).strict();
export type MeNotifications = z.infer<typeof MeNotifications>;

export const MeNotificationsUpdate = z.object({ decisionMails: z.boolean() }).strict();
export type MeNotificationsUpdate = z.infer<typeof MeNotificationsUpdate>;
